/* eslint-disable prettier/prettier */
import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { Messages } from '../../../gen-src/Localize';
import { AppUrls } from '../../app/AppUrls';
import { appUrlsSelector } from '../../app/selectors/AppUrlsSelector';
import { AppState } from '../../app/state/AppState';
import * as Layout from '../../common/styles/Layout';
import { classNames } from '../../common/styles/Utils';
import { changeContentByUrlPath } from '../actions/ChangeContentByUrlPathAction';
import { performSearch } from '../actions/PerformSearchAction';
import { ContentTypeFilterValue, ContentTypeFilterAllOption } from '../models/ContentTypeFilterValue';
import { NavigationService } from '../services/NavigationService';
import { ActionContainerStyles, DisplayModeChildrenStyle, HeaderStyles, SearchTitleStyle } from '../styles/SearchResultPageStyles';
import { ConnectedFilterBar } from '../components/filters/FilterBar';
import { ConnectedMixedContentTypeResultPage } from './MixedContentTypeResultPage';
import { NoResultPage } from './NoResultPage';
import { ConnectedSingleContentTypeResultPage } from './SingleContentTypeResultPage';
import { FilterQuery } from '../state/FilterState';
import { selectFilterQuery } from '../selectors/selectSingleContentTypeSearchResult';
import { ConnectedSortByDropdown } from './SortByDropdown';
import { ContentType } from '../models/ContentType';
import { OopsPage } from './OopsPage';
import { formatTotalSearchResultCount } from '../../common/utils/L10n';
import { SearchEPTService } from '../services/SearchEPTService';
import { EPTSessionType } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { ConnectedDisplayByDropdown } from '../components/DisplayByDropdown';
import { loadInitialDisplay } from '../actions/LoadInitialDisplayAction';
import { DisplayBy, DisplaySelection } from '../models/DisplaySelection';
import { ContentDefinitions } from '../models/contentDefinitions';

export const SortTestID = 'Sort';
export const SearchResultPageTestID = 'SearchResultPage'
export const SearchResultContentSectionTestID = 'SearchResultContentSection';
export const FilterBarContainerTestID = 'FilterBarContainer';
export const DisplayToggleContainerTestID = 'DisplayToggleContainer';
export const DisplayLabelTestID = 'DisplayLabel'
export const DisplayToggleTestID = 'DisplayToggle';
export interface SearchResultPageOwnProps {
  routeLocationSearch: string;
  routeLocationPathName: string;
}

export type SearchResultPageProps = SearchResultPageOwnProps & SearchResultPageStateProps & SearchResultPageDispatchProps;

export function SearchResultPage(props: SearchResultPageProps): JSX.Element {
  const {
    performSearch,
    urls,
    routeLocationSearch,
    routeLocationPathName,
    changeContentByUrlPath,
    selectedContentTypeFilterValue,
    filter,
    status,
    searchTerm,
  } = props;

  const [searchValue, setSearchValue] = React.useState(queryString.parse(routeLocationSearch)['search']);

  React.useEffect(() => {
    NavigationService.instance.initialize(urls);
  }, [urls]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    props.loadInitialDisplay(DisplayBy.getMixedViewDisplaySelection());
  }, []);

  React.useEffect(() => {
    const searchTerm = queryString.parse(routeLocationSearch)['search'];
    changeContentByUrlPath(searchTerm, routeLocationPathName, urls, routeLocationSearch);
    setSearchValue(queryString.parse(routeLocationSearch)['search']);
  }, [routeLocationPathName, urls, changeContentByUrlPath, routeLocationSearch]);

  React.useEffect(() => {
    performSearch(searchValue);
  }, [searchValue, filter.modifiedTime.id, filter.owner.id, filter.tag.id, performSearch]); //This should only be called if the actual search value or filter id changes not when sort changes

  React.useEffect(() => {
    if (FeatureFlagService.instance.getValue(FeatureFlag.searchEPT)) {
      if (status === SearchStatus.searchComplete) {
        SearchEPTService.instance.end(EPTSessionType.fullSearch, searchTerm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const totalCount =
    props.mixedContentTypeSearchResultCount === 0
      ? 0
      : selectedContentTypeFilterValue !== ContentTypeFilterAllOption
        ? props.singleContentTypeSearchResultCount !== undefined
          ? props.singleContentTypeSearchResultCount
          : null
        : props.mixedContentTypeSearchResultCount;
  return (
    <div className={classNames(Layout.absolute, Layout.fulfill, Layout.flexV)} role="region" aria-label="main" data-tb-test-id={SearchResultPageTestID}>
      <div className={HeaderStyles} role="heading" aria-level={1} aria-atomic="true" aria-live="polite" >
        <div className={SearchTitleStyle}>
          {totalCount !== null && props.status === SearchStatus.searchComplete ? (
            props.searchTerm ? (
              <>
                {Messages.SearchResultCountWithTerm({
                  COUNT_LOC: formatTotalSearchResultCount(totalCount),
                  COUNT: totalCount
                })}
                <b>{props.searchTerm}</b>
              </>
            ) : (
              Messages.SearchResultCountWithoutTerm({
                COUNT_LOC: formatTotalSearchResultCount(totalCount),
                COUNT: totalCount
              })
            )
          ) : props.status !== SearchStatus.hasError ? props.searchTerm ? (
            Messages.SearchingFor({ SEARCH_TERM: props.searchTerm })
          ) : (
            Messages.Searching()
          ) : <></>}
        </div>
      </div>
      <div className={classNames(Layout.flexH, Layout.flexItemCenter, ActionContainerStyles)}>
        <div className={classNames(Layout.flexAuto)}>
          {(props.status === SearchStatus.searchComplete || props.isLoadingFilterContent) ? (
            <div data-tb-test-id={FilterBarContainerTestID}>
              <ConnectedFilterBar />
            </div>
          ) : null}
        </div>
        <div>
          {selectedContentTypeFilterValue !== ContentTypeFilterAllOption && props.status === SearchStatus.searchComplete ? (
            <div className={classNames(Layout.flexH, Layout.flexItemCenter)} data-tb-test-id={SortTestID}>
              <label style={{ marginRight: '8px' }} htmlFor="search-sort-dropdown">{Messages.sortBy()}</label>
              <ConnectedSortByDropdown contentType={selectedContentTypeFilterValue as ContentType} />
            </div>
          ) : null}
        </div>
        <div style={{ marginLeft: '8px' }} >
          {FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224) && props.status === SearchStatus.searchComplete ? (
            <div className={classNames(Layout.flexH, Layout.flexItemCenter, DisplayModeChildrenStyle)} data-tb-test-id={DisplayToggleContainerTestID}>
              {selectedContentTypeFilterValue === ContentTypeFilterAllOption ? (<label style={{ marginRight: '8px' }} htmlFor="search-display-dropdown" data-tb-test-id={DisplayLabelTestID}>{Messages.showAs()}</label>) : null}
              {(selectedContentTypeFilterValue !== ContentTypeFilterAllOption && ContentDefinitions[selectedContentTypeFilterValue].defaultDisplayMode === 'list') ? null : <ConnectedDisplayByDropdown data-tb-test-id={DisplayToggleTestID} />}
            </div>
          ) : null}
        </div>
      </div>
      <div className={classNames(Layout.flexAuto, Layout.relative)} data-tb-test-id={SearchResultContentSectionTestID}>
        {props.status === SearchStatus.isSearching ? null : props.status === SearchStatus.hasError ? (<OopsPage />) : props.mixedContentTypeSearchResultCount ? (
          selectedContentTypeFilterValue === ContentTypeFilterAllOption ? (
            <ConnectedMixedContentTypeResultPage />
          ) : (
            <ConnectedSingleContentTypeResultPage />
          )
        ) : (
          <NoResultPage />
        )}
      </div>
    </div>
  );
}

export enum SearchStatus {
  isSearching = 'isSearching',
  hasError = 'hasError',
  searchComplete = 'searchComplete'
}

export interface SearchResultPageStateProps {
  status: SearchStatus,
  mixedContentTypeSearchResultCount: number;
  singleContentTypeSearchResultCount?: number;
  searchTerm: string;
  urls: AppUrls;
  selectedContentTypeFilterValue: ContentTypeFilterValue;
  isLoadingFilterContent: boolean;
  filter: FilterQuery;
}

export interface SearchResultPageDispatchProps {
  performSearch: (searchTerm: string) => void;
  changeContentByUrlPath: (searchTerm: string, urlPath: string, urls: AppUrls, filters: string) => void;
  loadInitialDisplay: (displaySelection: DisplaySelection) => void;
}

const mapStateToProps = (state: AppState) => {
  return {
    status: state.search.status,
    mixedContentTypeSearchResultCount: state.search.totalCount,
    singleContentTypeSearchResultCount: state.singleContentType.searchResults?.totalCount,
    searchTerm: state.search.searchTerm,
    urls: appUrlsSelector(state),
    selectedContentTypeFilterValue: state.search.selectedContentTypeFilterValue,
    isLoadingFilterContent: state.filters.isLoadingFilterContent,
    filter: selectFilterQuery(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      performSearch: performSearch,
      changeContentByUrlPath: changeContentByUrlPath,
      loadInitialDisplay: loadInitialDisplay,
    },
    dispatch
  );

export const ConnectedSearchResultPage: React.ComponentClass<SearchResultPageOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchResultPage);
